import React, { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, DatePicker, Input, Modal, Select, Table, Typography } from "antd";
import { SaveOutlined, SwapOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { categories, clientName, dimensionsText, findClient, money, openStayFor } from "../../mocks/carpets";
import CleanStatus from "./CleanStatus";
import dateColumn from "./dateColumn";
import ShopStatus from "./ShopStatus";
import { useCarpetPreview } from "./context";

const { Title } = Typography;

const lastMovement = (state, carpetId) => {
  const open = openStayFor(state, carpetId);
  if (open) return open.inAt;
  const left = state.stays
    .filter((stay) => stay.carpetId === carpetId && stay.outAt)
    .sort((a, b) => new Date(b.outAt) - new Date(a.outAt))[0];
  return left ? left.outAt : null;
};

export default function ClientCarpets() {
  const intl = useIntl();
  const { state, setCleaned, updateBoxes } = useCarpetPreview();
  const [clientId, setClientId] = useState(undefined);
  const [status, setStatus] = useState("all");
  const [ref, setRef] = useState("");
  const [category, setCategory] = useState(undefined);
  const [box, setBox] = useState("");
  const [carpetId, setCarpetId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [range, setRange] = useState([moment().subtract(1, "year"), moment()]);

  const carpets = useMemo(() => state.carpets.filter((carpet) => {
    const open = openStayFor(state, carpet.id);
    if (clientId && carpet.clientId !== clientId) return false;
    if (status === "in" && !open) return false;
    if (status === "out" && open) return false;
    if (ref && !String(carpet.ref).includes(ref.trim())) return false;
    if (category && carpet.category !== category) return false;
    const boxText = (open && open.storageBox) || "";
    if (box && !boxText.toLowerCase().includes(box.trim().toLowerCase())) return false;
    return true;
  }), [state, clientId, status, ref, category, box]);
  const history = state.stays.filter((stay) => stay.carpetId === carpetId);
  const selectedCarpet = state.carpets.find((carpet) => carpet.id === carpetId);

  const counts = useMemo(() => {
    const [from, to] = range || [];
    const start = from ? from.clone().startOf("day").valueOf() : null;
    const end = to ? to.clone().endOf("day").valueOf() : null;
    const inRange = (value) => {
      if (!value || start == null || end == null) return false;
      const time = new Date(value).getTime();
      return time >= start && time <= end;
    };
    return {
      inShop: state.stays.filter((stay) => !stay.outAt).length,
      imported: state.stays.filter((stay) => inRange(stay.inAt)).length,
      exported: state.stays.filter((stay) => inRange(stay.outAt)).length,
    };
  }, [state, range]);

  const saveBox = (stay) => {
    const storageBox = drafts[stay.id] != null ? drafts[stay.id] : stay.storageBox;
    updateBoxes([{ stayId: stay.id, storageBox }]);
    setDrafts((current) => {
      const next = { ...current };
      delete next[stay.id];
      return next;
    });
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>{intl.formatMessage({ id: "carpets" })}</Title>
      <div style={{ marginBottom: 12 }}>
        <strong>{intl.formatMessage({ id: "total" })}: {counts.inShop}</strong>
        <span style={{ marginLeft: 16 }}>{intl.formatMessage({ id: "imported" })}: {counts.imported}</span>
        <span style={{ marginLeft: 16 }}>{intl.formatMessage({ id: "exported" })}: {counts.exported}</span>
        <DatePicker.RangePicker
          className="no-print"
          style={{ marginLeft: 16 }}
          value={range}
          onChange={(value) => setRange(value || [null, null])}
        />
      </div>
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <Select
          allowClear
          placeholder={intl.formatMessage({ id: "client" })}
          value={clientId}
          style={{ width: 220 }}
          onChange={(value) => { setClientId(value); setCarpetId(null); }}
        >
          {state.clients.map((client) => (
            <Select.Option key={client.id} value={client.id}>#{client.ref} {clientName(client)}</Select.Option>
          ))}
        </Select>
        <Select value={status} style={{ width: 140 }} onChange={setStatus}>
          <Select.Option value="all">{intl.formatMessage({ id: "all" })}</Select.Option>
          <Select.Option value="in">{intl.formatMessage({ id: "inShop" })}</Select.Option>
          <Select.Option value="out">{intl.formatMessage({ id: "checkedOut" })}</Select.Option>
        </Select>
        <Input placeholder={intl.formatMessage({ id: "ref" })} value={ref} style={{ width: 100 }} onChange={(event) => setRef(event.target.value)} />
        <Select
          allowClear
          placeholder={intl.formatMessage({ id: "category" })}
          value={category}
          style={{ width: 140 }}
          onChange={setCategory}
        >
          {categories.map((item) => (
            <Select.Option key={item} value={item}>{intl.formatMessage({ id: item })}</Select.Option>
          ))}
        </Select>
        <Input placeholder={intl.formatMessage({ id: "storageBox" })} value={box} style={{ width: 120 }} onChange={(event) => setBox(event.target.value)} />
      </div>
      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="id"
        dataSource={carpets}
        columns={[
          { title: intl.formatMessage({ id: "ref" }), dataIndex: "ref", width: 70 },
          { title: intl.formatMessage({ id: "client" }), render: (carpet) => {
            const client = findClient(state, carpet.clientId);
            return client ? `#${client.ref} ${clientName(client)}` : "";
          } },
          { title: intl.formatMessage({ id: "category" }), render: (carpet) => intl.formatMessage({ id: carpet.category }) },
          { title: intl.formatMessage({ id: "widthXLength" }), render: (carpet) => dimensionsText(carpet) },
          { title: intl.formatMessage({ id: "description" }), dataIndex: "description" },
          {
            title: intl.formatMessage({ id: "estimatedPrice" }),
            width: 130,
            render: (carpet) => money(carpet.price),
          },
          {
            title: intl.formatMessage({ id: "jobPrice" }),
            width: 130,
            render: (carpet) => {
              const open = openStayFor(state, carpet.id);
              const stay = open || state.stays
                .filter((item) => item.carpetId === carpet.id && item.outAt)
                .sort((a, b) => new Date(b.outAt) - new Date(a.outAt))[0];
              return stay ? money(stay.jobPrice) : "";
            },
          },
          {
            title: intl.formatMessage({ id: "status" }),
            width: 90,
            render: (carpet) => <ShopStatus inShop={!!openStayFor(state, carpet.id)} />,
          },
          {
            title: intl.formatMessage({ id: "cleanStatus" }),
            width: 150,
            render: (carpet) => {
              const open = openStayFor(state, carpet.id);
              const stay = open || state.stays
                .filter((item) => item.carpetId === carpet.id && item.outAt)
                .sort((a, b) => new Date(b.outAt) - new Date(a.outAt))[0];
              if (!stay) return "";
              const action = intl.formatMessage({ id: "cleanStatus" });
              return (
                <span style={{ display: "flex", gap: 4, alignItems: "center" }} onClick={(event) => event.stopPropagation()}>
                  <CleanStatus cleaned={stay.cleaned} />
                  {open && (
                    <Button
                      size="small"
                      type="primary"
                      icon={<SwapOutlined />}
                      aria-label={action}
                      title={action}
                      onClick={() => setCleaned(stay.id, !stay.cleaned)}
                    />
                  )}
                </span>
              );
            },
          },
          dateColumn(intl.formatMessage({ id: "date" }), (carpet) => {
            const when = lastMovement(state, carpet.id);
            return when ? moment(when).format("DD/MM/YYYY HH:mm") : "";
          }),
          {
            title: intl.formatMessage({ id: "storageBox" }),
            width: 160,
            render: (carpet) => {
              const open = openStayFor(state, carpet.id);
              if (!open) return "";
              const value = drafts[open.id] != null ? drafts[open.id] : (open.storageBox || "");
              const changed = drafts[open.id] != null && drafts[open.id] !== (open.storageBox || "");
              const saveLabel = intl.formatMessage({ id: "save" });
              return (
                <span style={{ display: "flex", gap: 4 }} onClick={(event) => event.stopPropagation()}>
                  <Input
                    size="small"
                    value={value}
                    onChange={(event) => {
                      const next = event.target.value;
                      setDrafts((current) => ({ ...current, [open.id]: next }));
                    }}
                  />
                  <Button
                    size="small"
                    type="primary"
                    icon={<SaveOutlined />}
                    aria-label={saveLabel}
                    title={saveLabel}
                    disabled={!changed}
                    onClick={() => saveBox(open)}
                  />
                </span>
              );
            },
          },
          {
            width: 48,
            render: (carpet) => {
              const label = intl.formatMessage({ id: "entries" });
              return (
                <Button
                  size="small"
                  type="primary"
                  icon={<UnorderedListOutlined />}
                  aria-label={label}
                  title={label}
                  onClick={() => setCarpetId(carpet.id)}
                />
              );
            },
          },
        ]}
      />
      <Modal
        title={selectedCarpet ? `${intl.formatMessage({ id: "entries" })} #${selectedCarpet.ref}` : ""}
        visible={!!carpetId}
        width={960}
        footer={null}
        onCancel={() => setCarpetId(null)}
        destroyOnClose
      >
        <Table
          size="small"
          bordered
          pagination={false}
          rowKey="id"
          dataSource={history}
          columns={[
            dateColumn(intl.formatMessage({ id: "inAt" }), (stay) => moment(stay.inAt).format("DD/MM/YYYY HH:mm")),
            dateColumn(intl.formatMessage({ id: "outAt" }), (stay) => (stay.outAt ? moment(stay.outAt).format("DD/MM/YYYY HH:mm") : "")),
            { title: intl.formatMessage({ id: "cleanStatus" }), width: 110, render: (stay) => <CleanStatus cleaned={stay.cleaned} /> },
            { title: intl.formatMessage({ id: "jobPrice" }), width: 130, render: (stay) => money(stay.jobPrice) },
            { title: intl.formatMessage({ id: "storageBox" }), dataIndex: "storageBox" },
            { title: intl.formatMessage({ id: "inNotes" }), dataIndex: "inNotes" },
            { title: intl.formatMessage({ id: "outNotes" }), dataIndex: "outNotes" },
          ]}
        />
      </Modal>
    </div>
  );
}
